import { useMemo } from "react";
import {
  FieldValues,
  Path,
  useController,
  useFormContext,
} from "react-hook-form";

import {
  Autocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  ChipProps,
  TextField,
} from "@mui/material";

import { OptionIf } from "../FiltersBuilder";
import { Label, Wrapper } from "./AutocompleteFilter.styled";

export interface AutocompleteFilterProps<T extends FieldValues> {
  label: string;
  options: OptionIf[];
  name?: Path<T>;
  placeholder: string;
  ChipProps?: ChipProps<"div">;
}

export const AutocompleteFilter = <T extends FieldValues>({
  label,
  options,
  name,
  placeholder,
  ChipProps,
}: AutocompleteFilterProps<T>) => {
  const { control } = useFormContext();
  const {
    field: { value, onChange },
  } = useController({ control, name: name ?? "tags" });
  const selected = value as string[] | undefined;

  const toggleSelect = (id: string) => {
    if (selected?.includes(id)) return onChange(selected.filter(s => s !== id));
    return onChange([...(selected ?? []), id]);
  };

  const clearAll = () =>
    onChange(
      selected?.filter(option => !options.some(({ id }) => id === option)),
    );

  const selectedOptions = useMemo(
    () => options.filter(option => selected?.some(id => option.id === id)),
    [options, selected],
  );

  const handleChange = (
    event: React.SyntheticEvent,
    value: OptionIf<string>[],
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<OptionIf<string>> | undefined,
  ) => {
    if (reason === "clear") clearAll();
    if (details) toggleSelect(details?.option.id);
  };

  return (
    <Wrapper>
      <Label>{label}</Label>
      <Autocomplete
        size="small"
        options={options}
        value={selectedOptions}
        onChange={handleChange}
        multiple
        renderInput={params => (
          <TextField {...params} placeholder={placeholder} />
        )}
        ChipProps={{
          color: "primary",
          ...ChipProps,
        }}
      />
    </Wrapper>
  );
};
